import React, { useEffect } from 'react';
import { Authenticator } from '@aws-amplify/ui-react-native';
import SharedLoginSection from './SharedLoginSection';
import { I18n } from 'aws-amplify/utils';
import { RedirectToAppOnLogin } from './RedirectToAppOnLogin';
import MaxWidthRestrictedContent from '../v2BaseComponents/MaxWidthRestrictedContent';
import Text from '../v2BaseComponents/Text';
import { getIntl } from '../../utils/intl';
import { getSpacing } from '../../utils/spacing';
import { colors } from '../../utils/theme';
import AmplifyAuthenticationFormFields from './AmplifyAuthenticationFormFields';
import Stack from '../v2BaseComponents/Stack';
import TitleLogo from './TitleLogo';
import { useIsMobileLayout } from '../../hooks/useIsMobileLayout';
import Button from '../v2BaseComponents/Button';
import * as Linking from 'expo-linking';
import { privacyPolicyUrl, termsOfServiceUrl } from '../../utils/constants';
import Divider from '../v2BaseComponents/Divider';
import { useCapturedUrlUserType, useOrganizationClientInvitationId } from '../../hooks/useUrlAppParams';
import { useMaybeAvatar } from '../../hooks/useAvatar';
import urls from '../../utils/urls';
import SkeletonLoadingContainer, {
    RenderChildrenWithExistingContainerDimensions,
} from '../v2BaseComponents/SkeletonLoadingContainer';
import ScaledImage from '../v2BaseComponents/ScaledImage';
import { useIsUserLoggedIn } from '../../hooks/useIsUserLoggedIn';
import { useIsFocused } from '@react-navigation/native';
import { useResetApp } from '../ResetAppProvider';
import SplashScreenBackground from '../v2BaseComponents/SplashScreenBackground';
import { View } from 'react-native';
import Link from '../v2BaseComponents/Link';
import { useNavigateToConsumerApp, useNavigateToProApp } from '../../utils/appNavigationNames';
import { useEnvironment } from '../../hooks/useEnvironment';
import { preloadRecaptcha, signUpWithRecaptcha } from '../../utils/recaptcha';

// a bit of a hack to get the authenticator to use our custom messaging
const header = 'Welcome to Primary Record!';
I18n.putVocabularies({
    en: {
        'We Emailed You': header,
        'Your code is on the way. To log in, enter the code we emailed to':
            'Your confirmation code has been sent. Please check your inbox or spam folder at',
        'It may take a minute to arrive': '',
        /*
            The authenticator can get in a weird state.  The user can:
            1. Initiate signup with an email and password
            2. Bail before entering the registration code
            3. Initiate SIGN IN with an unverified email
            4. Get prompted to enter a verification code

            amplify doesn't seem to know what verification method to use, so it defaults to text message.
            (I dug into the source code: https://github.com/aws-amplify/amplify-ui/blob/4775ee646559bd7ad0a775c80d8e0a7854fb470d/packages/react/src/components/Authenticator/ConfirmSignUp/ConfirmSignUp.tsx#L26 .  It appears that codeDeliveryDetails is undefined in this flow)

            Simply override text-based messaging with email-based messaging
        */
        'We Texted You': header,
        'Your code is on the way. To log in, enter the code we sent you':
            'Your confirmation code has been sent. Please check your inbox or spam folder at',
    },
});

const intl = getIntl('AmplifyAuthentication', {
    signIn: 'Sign In',
    signUp: 'Sign Up',
    welcome: 'Welcome to Primary Record!',
    resetPassword: 'Reset Password',
    trademark: 'Primary Record® is a registered trademark of Primary Record Inc. All rights reserved.',
    privacyPolicy: 'Privacy Policy',
    termsOfService: 'Terms of Service',
    organizationLogo: 'Organization Logo',
    organizationLogoSubtext: 'Powered by Primary Record',
    professionalSignIn: 'Professional Sign In',
    professionalSignUp: 'Professional Sign Up',
    redirectToProfessionalApp: 'Looking for the Professional Edition?',
    redirectToFamilyApp: 'Looking for the Family Edition?',
});

export type AuthenticatedRootOwnProps = {};

export type AuthenticatedRootProps = AuthenticatedRootOwnProps;

type AmplifyAuthenticationProps = {
    state: 'signIn' | 'signUp';
};

const AmplifyAuthenticationHeader: React.FC<{ text: string }> = ({ text }) => {
    const organizationClientInvitationId = useOrganizationClientInvitationId();
    const maybeOrganizationLogo = useMaybeAvatar({
        avatarAPIUrl: organizationClientInvitationId
            ? urls.api.publicOrganizations.clientInvitationLogo(organizationClientInvitationId)
            : null,
        usePublicAPI: true,
    });

    const renderLogo = () => {
        const fallbackLogo = <TitleLogo width="100%" />;

        if (!maybeOrganizationLogo) return fallbackLogo;

        const renderSkeletonChildren: RenderChildrenWithExistingContainerDimensions = (containerDimensions) => {
            if (maybeOrganizationLogo.isLoadingAvatar || !maybeOrganizationLogo.avatar) return fallbackLogo;

            const scaledImageSkeletonRatio = containerDimensions.height / (containerDimensions.width || 1);

            return (
                <Stack vertical justify="center" align="flex-start" space={1}>
                    <ScaledImage
                        uri={maybeOrganizationLogo.avatar.base64}
                        restrict="width"
                        alt={intl('organizationLogo')}
                        containerStyle={{
                            width: containerDimensions.width,
                        }}
                        loaderRatio={scaledImageSkeletonRatio}
                    />
                    <Text variant="caption-1" alignText="center">
                        {intl('organizationLogoSubtext')}
                    </Text>
                </Stack>
            );
        };

        return (
            <SkeletonLoadingContainer isLoading={maybeOrganizationLogo.isLoadingAvatar}>
                {renderSkeletonChildren}
            </SkeletonLoadingContainer>
        );
    };

    return (
        <Stack vertical fullWidth align="center" space={getSpacing(8)}>
            {renderLogo()}
            <Text
                variant="h3"
                alignText="center"
                style={{
                    marginBottom: getSpacing(7),
                }}
            >
                {text}
            </Text>
        </Stack>
    );
};

const MaybeProAuthenticationHeaderMessaging: React.FC<{ state: 'signIn' | 'signUp' }> = ({ state }) => {
    const isProAppRegistration = useCapturedUrlUserType() === 'professional';

    let text = intl(state);
    let navigationLinkText = intl('redirectToProfessionalApp');
    let toggleNavigationToUserApp = useNavigateToProApp();
    const navigateToFamilyApp = useNavigateToConsumerApp();

    if (isProAppRegistration) {
        text = intl(state === 'signIn' ? 'professionalSignIn' : 'professionalSignUp');
        navigationLinkText = intl('redirectToFamilyApp');
        toggleNavigationToUserApp = navigateToFamilyApp;
    }

    return (
        <React.Fragment>
            <AmplifyAuthenticationHeader text={text} />
            <View
                style={{
                    alignItems: 'center',
                    width: '100%',
                    paddingBottom: getSpacing(16),
                }}
            >
                <Link
                    onPress={() => toggleNavigationToUserApp(state === 'signIn' ? 'login' : 'signup')}
                    variant="body-1"
                    alignText="center"
                >
                    {navigationLinkText}
                </Link>
            </View>
        </React.Fragment>
    );
};

const SignUpHeader = () => <MaybeProAuthenticationHeaderMessaging state="signUp" />;
const SignInHeader = () => <MaybeProAuthenticationHeaderMessaging state="signIn" />;
const ResetPasswordHeader = () => <AmplifyAuthenticationHeader text={intl('resetPassword')} />;
const ConfirmSignUpHeader = () => <AmplifyAuthenticationHeader text={intl('welcome')} />;
const ConfirmResetPasswordHeader = () => <AmplifyAuthenticationHeader text={intl('resetPassword')} />;
const SignInFooter = () => <SharedLoginSection />;
const SignUpFooter = () => <SharedLoginSection isCreateAccount />;

export const AmplifyAuthentication: React.FC<AmplifyAuthenticationProps> = ({ state }) => {
    const { isUserLoggedIn, recheckIsUserLoggedIn } = useIsUserLoggedIn();
    const isFocused = useIsFocused();
    const resetApp = useResetApp();
    const environment = useEnvironment();

    /**
     * If navigating back after signup, amplify will just display an empty screen.
     *
     * If getting focus, recheck if user is logged in.  If logged in and focused, reset the app.
     */
    useEffect(() => {
        if (isFocused) {
            recheckIsUserLoggedIn();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFocused]);

    useEffect(() => {
        if (isFocused && isUserLoggedIn) {
            const timeout = setTimeout(() => {
                resetApp();
            }, 1000);

            return () => {
                clearTimeout(timeout);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFocused, isUserLoggedIn]);

    useEffect(() => {
        if (state === 'signUp') {
            preloadRecaptcha(environment);
        }
    }, [environment, state]);

    const isMobile = useIsMobileLayout();

    return (
        <Authenticator.Provider>
            <SplashScreenBackground>
                <Stack vertical fullWidth fullHeight align="center">
                    <MaxWidthRestrictedContent
                        maxWidth={400}
                        style={{ height: '100%', flex: 1 }}
                        contentContainerStyle={{
                            flex: 1,
                            justifyContent: 'center',
                            height: '100%',
                            alignItems: 'center',
                        }}
                    >
                        <Authenticator
                            socialProviders={[]}
                            initialState={state}
                            services={{
                                handleSignUp: (input) => signUpWithRecaptcha(input, environment),
                            }}
                            Container={({ children, ...toPass }) => (
                                <Authenticator.Container
                                    {...toPass}
                                    style={{
                                        backgroundColor: 'transparent',
                                        paddingHorizontal: isMobile ? getSpacing(4) : getSpacing(2),
                                        flex: 1,
                                    }}
                                >
                                    {children}
                                    <Stack
                                        vertical
                                        fullWidth
                                        justify="center"
                                        align="center"
                                        style={{
                                            paddingBottom: getSpacing(isMobile ? 8 : 16),
                                            paddingHorizontal: getSpacing(2),
                                        }}
                                    >
                                        <Text variant="inactive-tab" alignText="center">
                                            {intl('trademark')}
                                        </Text>
                                        <Stack
                                            horizontal
                                            fullWidth
                                            align="center"
                                            justify="center"
                                            space={getSpacing(2)}
                                        >
                                            <Button
                                                variant="text"
                                                onPress={() => {
                                                    Linking.openURL(privacyPolicyUrl);
                                                }}
                                                title={intl('privacyPolicy')}
                                                size="sm"
                                            />
                                            <Divider
                                                vertical
                                                style={{
                                                    height: '60%',
                                                }}
                                            />
                                            <Button
                                                variant="text"
                                                onPress={() => {
                                                    Linking.openURL(termsOfServiceUrl);
                                                }}
                                                title={intl('termsOfService')}
                                                size="sm"
                                            />
                                        </Stack>
                                    </Stack>
                                </Authenticator.Container>
                            )}
                            components={{
                                SignIn: (props) => {
                                    return (
                                        <Authenticator.SignIn
                                            {...props}
                                            style={{
                                                color: colors['Grey'],
                                            }}
                                            hideSignUp
                                            FormFields={AmplifyAuthenticationFormFields.SignIn}
                                            Header={SignInHeader}
                                            Footer={SignInFooter}
                                        />
                                    );
                                },
                                SignUp: (props) => (
                                    <Authenticator.SignUp
                                        {...props}
                                        hideSignIn
                                        Header={SignUpHeader}
                                        FormFields={AmplifyAuthenticationFormFields.SignUp}
                                        Footer={SignUpFooter}
                                    />
                                ),
                                ConfirmSignUp: (props) => (
                                    <Authenticator.ConfirmSignUp
                                        {...props}
                                        FormFields={AmplifyAuthenticationFormFields.ConfirmSignUp}
                                        Header={ConfirmSignUpHeader}
                                    />
                                ),
                                ForgotPassword: (props) => (
                                    <Authenticator.ForgotPassword
                                        {...props}
                                        FormFields={AmplifyAuthenticationFormFields.ResetPassword}
                                        Header={ResetPasswordHeader}
                                    />
                                ),
                                ConfirmResetPassword: (props) => (
                                    <Authenticator.ConfirmResetPassword
                                        {...props}
                                        FormFields={AmplifyAuthenticationFormFields.ConfirmResetPassword}
                                        Header={ConfirmResetPasswordHeader}
                                    />
                                ),
                            }}
                        >
                            <RedirectToAppOnLogin />
                        </Authenticator>
                    </MaxWidthRestrictedContent>
                </Stack>
            </SplashScreenBackground>
        </Authenticator.Provider>
    );
};
